import { segment } from '@actions/analytics';
import Button from '@base/Button/Button';
import H3 from '@base/H3/H3';
import P2 from '@base/P2/P2';
import { evtNameCategoryMap, programmeTypes } from '@constants/index';
import {
  ADD_TO_PLANNER_PREFERENCE_QUESTION,
  COURSE_KEYS,
  TEST_SERIES_KEYS
} from '@constants/segment';
import styled from '@emotion/styled';
import useAllTypesOfCourseEventProps from '@hooks/useAllTypesOfCourseEventProps';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { Spacings } from '@styles/index';
import {
  generateStartOfDay,
  getLabelForTimeinDay,
  weekDays
} from '@utils/dates';
import { format as formatDate } from 'date-fns';
import { FC, useState } from 'react';
import {
  EditCustomizationSteps,
  AllCustomizationSteps,
  CustomizationStepsWithoutLessons,
  CustomisationStepsInfo
} from './AllCustomizationSteps';
import StepCustomisationCarousel from './StepCustomisationCarousel';
import CourseTimeSelector from './CourseTimeSelector';
import IncludeCompletedLessonsSection from './IncludeCompletedLessonsSection';
import SelectDateForPlannerSection from './SelectDateForPlannerSection';
import SelectDaysSection from './SelectDaysSection';
import { IBaseAddDrawerProps } from './SimpleAddDrawer';

const HeadingWrapper = styled(H3)`
  margin-top: ${Spacings.SPACING_6B};
  margin-bottom: ${Spacings.SPACING_10B};
`;

const ErrorText = styled(P2)`
  color: var(--color-i-red);
  margin-top: ${Spacings.SPACING_4B};
`;

interface ICusomizeMoreAddDrawrContentProps extends IBaseAddDrawerProps {
  lessonCount: number;
  hasWatchedLessons: boolean;
}

const PreviousNextContainer = styled.div`
  margin-top: ${Spacings.SPACING_12B};
  display: flex;
  align-items: center;
  gap: ${Spacings.SPACING_4B};
`;

const PreviousButton = styled(Button)<any>`
  color: var(--color-text-primary);
  background-color: var(--color-base-1);
`;

const NextButton = styled(Button)<any>`
  background-color: var(--color-text-primary);
  color: var(--color-base-1);

  &:hover,
  &:active {
    background-color: var(--color-text-primary);
  }
`;

const CustomizeMoreDrawerContent: FC<ICusomizeMoreAddDrawrContentProps> = ({
  isEditFlow = false,
  goalUid,
  courseType,
  plusCourse,
  onClickConfirm,
  plannerSchedule,
  lessonCount,
  hasWatchedLessons
}) => {
  let CustomizationSteps = AllCustomizationSteps;
  if (isEditFlow) {
    CustomizationSteps = EditCustomizationSteps;
  } else if (!hasWatchedLessons) {
    CustomizationSteps = CustomizationStepsWithoutLessons;
  }
  const goalSegmentData = useGoalEventProps({ goalUID: goalUid });
  const segmentData = useAllTypesOfCourseEventProps({ course: plusCourse });
  const eventKeys =
    courseType === programmeTypes.TESTSERIES ? TEST_SERIES_KEYS : COURSE_KEYS;
  // 540 = 9 * 60 i.e., 9 hours since start of day = 9AM is default time
  const [selectedTime, setSelectedTime] = useState(
    plannerSchedule?.daily_item_schedule_at || 540
  );
  const handleTimeChange = (timeInMins: number) => setSelectedTime(timeInMins);

  const plannerScheduleDate = plannerSchedule?.start_date ?? new Date();
  const plannerScheduleDateStart = generateStartOfDay(plannerScheduleDate);
  const [startDate, setStartDate] = useState(plannerScheduleDateStart);

  const [includeCompletedLessons, setIncludeCompletedLessons] = useState(false);
  const handleIncludeCompletedLessons = (includeAll: boolean) => {
    setIncludeCompletedLessons(includeAll);
    goToNextStep();
  };

  const [days, setDays] = useState<number[]>(
    plannerSchedule?.week_days || [0, 1, 2, 3, 4, 5, 6]
  );
  const handleUpdateDays = (updatedDays: number[]) => {
    setDays(updatedDays);
    if (updatedDays.length === 0) {
      const daysStepInfo = CustomizationSteps.find((x) => x.id === 'days');
      setErrorMessage(daysStepInfo?.error);
    } else setErrorMessage(null);
  };

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [progressStep, setProgressStep] = useState(0);
  const isLastStep = progressStep === CustomizationSteps.length - 1;
  const {
    title = '',
    id: stepId,
    showPreviousNext = false,
    error: stepErrorMessage
  } = CustomizationSteps[progressStep];

  const handleSendPreferencesQuestionEvent = () => {
    let responseText = '';
    if (stepId === CustomisationStepsInfo.INCLUDE_ALL_LESSONS.id) {
      responseText = includeCompletedLessons
        ? 'Add all sessions'
        : 'Add unwatched sessions';
    } else if (stepId === CustomisationStepsInfo.START_DATE.id) {
      responseText = formatDate(startDate, 'dd MMM');
    } else if (stepId === CustomisationStepsInfo.DAYS.id && days.length > 0) {
      responseText = days.map((d) => weekDays[d]).join(',');
    } else if (stepId === CustomisationStepsInfo.LESSON_TIME.id) {
      responseText = getLabelForTimeinDay(selectedTime / 60);
    }
    segment.track(
      ADD_TO_PLANNER_PREFERENCE_QUESTION,
      {
        ...goalSegmentData,
        ...segmentData,
        is_default_preference: false,
        question_content: title,
        response_text: responseText,
        is_first_time: !isEditFlow,
        is_skipped: false
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      eventKeys
    );
  };

  const goToPreviousStep = () => {
    if (errorMessage) setErrorMessage(null);
    if (progressStep >= 0) setProgressStep(progressStep - 1);
  };

  const goToNextStep = () => {
    handleSendPreferencesQuestionEvent();
    if (isLastStep) {
      onClickConfirm({
        selectedChoice: selectedTime,
        include_completed_lessons: includeCompletedLessons,
        days,
        start_date: startDate
      });
    } else if (progressStep < CustomizationSteps.length) {
      if (stepId === CustomisationStepsInfo.DAYS.id && days.length === 0) {
        setErrorMessage(stepErrorMessage);
      } else {
        setProgressStep(progressStep + 1);
      }
    }
  };

  const renderCurrentStep = () => {
    if (stepId === CustomisationStepsInfo.INCLUDE_ALL_LESSONS.id) {
      return (
        <IncludeCompletedLessonsSection
          lessonCount={lessonCount}
          handleIncludeCompletedLessons={handleIncludeCompletedLessons}
        />
      );
    }
    if (stepId === CustomisationStepsInfo.START_DATE.id) {
      return (
        <SelectDateForPlannerSection
          startDate={startDate}
          handleStartDateChange={setStartDate}
        />
      );
    }
    if (stepId === CustomisationStepsInfo.DAYS.id) {
      return (
        <SelectDaysSection days={days} handleUpdateDays={handleUpdateDays} />
      );
    }
    if (stepId === CustomisationStepsInfo.LESSON_TIME.id) {
      return (
        <CourseTimeSelector
          selectedChoice={selectedTime}
          onChange={handleTimeChange}
        />
      );
    }
    return null;
  };

  const isFirstStep = stepId !== CustomizationSteps[0].id;

  return (
    <>
      <StepCustomisationCarousel
        currentStepId={stepId}
        steps={CustomizationSteps}
      />
      <HeadingWrapper>
        {title}
        {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
      </HeadingWrapper>
      {renderCurrentStep()}
      {showPreviousNext && (
        <PreviousNextContainer>
          {isFirstStep && (
            <PreviousButton
              variant="hollow"
              onClick={goToPreviousStep}
              label="Previous"
            />
          )}
          <NextButton
            onClick={goToNextStep}
            label={isLastStep ? 'Done' : 'Next'}
          />
        </PreviousNextContainer>
      )}
    </>
  );
};

export default CustomizeMoreDrawerContent;
